import React, { useMemo, useState } from 'react';
import { Box, Typography, Stack, Paper, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import MapGridSelector from './MapGridSelector';
import RadiusSelector from './RadiusSelector';
import HeatmapPreviewMap from '../Heatmap/HeatmapPreviewMap';

// Mobile-First Styled Components
const ConfigContainer = styled(Stack)(({ theme }) => ({
  gap: '20px',
  direction: 'column',
  alignItems: 'stretch',
  [theme.breakpoints.up('sm')]: {
    gap: '24px',
  },
}));

const ConfigRow = styled(Stack)(({ theme }) => ({
  direction: 'column',
  gap: '20px',
  alignItems: 'stretch',
  [theme.breakpoints.up('sm')]: {
    gap: '24px',
  },
}));

const ConfigColumn = styled(Stack)(({ theme }) => ({
  gap: '16px',
  flex: 1,
  [theme.breakpoints.up('sm')]: {
    gap: '20px',
  },
  [theme.breakpoints.up('md')]: {
    gap: '24px',
  },
}));

const SectionCard = styled(Paper)(({ theme }) => ({
  borderRadius: '12px',
  border: '0.6px solid #E2E8F0',
  padding: '16px',
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  [theme.breakpoints.up('sm')]: {
    padding: '20px',
  },
  [theme.breakpoints.up('md')]: {
    padding: '24px',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#0f172a',
  marginBottom: '12px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '15px',
    marginBottom: '16px',
  },
}));

const MapWrapper = styled(Box)(({ theme }) => ({
  borderRadius: '12px',
  overflow: 'hidden',
  border: '1px solid #E2E8F0',
  // Ensure touch interactions work on mobile
  touchAction: 'pan-x pan-y',
}));

const MapConfigurationSection = ({ formData, onFormChange, isMobile, isTablet }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const selectedUnit = formData.radiusUnit || 'Km';

  const handleUnitChange = (unit) => {
    onFormChange('radiusUnit', unit);
  };

  const gridSizeNumber = useMemo(() => {
    if (!formData?.mapGrid) return 5;
    const m = /([0-9]+)\*([0-9]+)/.exec(formData.mapGrid);
    return m ? Number(m[1]) : 5;
  }, [formData?.mapGrid]);

  // Responsive map height
  const getMapHeight = () => {
    if (isSmallScreen) return 280;
    if (isMediumScreen) return 340;
    return 380;
  };

  return (
    <ConfigContainer>
      <SectionCard elevation={0}>
        <SectionTitle>Map Configuration</SectionTitle>
        <ConfigRow>
          <ConfigColumn>
            {/* Grid Selector */}
            <MapGridSelector
              value={formData.mapGrid}
              onChange={(value) => onFormChange('mapGrid', value)}
              isMobile={isMobile}
              isTablet={isTablet}
            />

            {/* Map Preview */}
            <MapWrapper>
              <HeatmapPreviewMap
                center={formData?.center || { lat: 28.6139, lng: 77.2090 }}
                gridSize={gridSizeNumber}
                radiusKm={formData.radiusValue || 1}
                height={getMapHeight()}
              />
            </MapWrapper>

            {/* Radius Selector */}
            <RadiusSelector
              value={formData.radiusValue}
              selectedUnit={selectedUnit}
              onValueChange={(value) => onFormChange('radiusValue', value)}
              onUnitChange={handleUnitChange}
              isMobile={isMobile}
              isTablet={isTablet}
            />
          </ConfigColumn>
        </ConfigRow>
      </SectionCard>
    </ConfigContainer>
  );
};

export default MapConfigurationSection;
